import React, { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { api, formatApiError } from "../lib/api";
import { toast } from "sonner";
import {
  ArrowRight, CalendarDays, Car, SprayCan, PaintRoller, UserRound,
  Utensils, Camera, Wrench, Home as HomeIcon, Truck, ShieldCheck,
  Clock, Gem, Award, CheckCircle2,
} from "lucide-react";

const HERO_IMG = "https://images.unsplash.com/photo-1522255272218-7ac5249be344?crop=entropy&cs=srgb&fm=jpg&q=85&w=2000";

const HOME_SERVICE_CARDS = [
  { slug: "detailing", label: "Detailing", desc: "Premium vehicle detailing services to keep your car looking pristine.", icon: Car },
  { slug: "cleaning", label: "Cleaning", desc: "Professional cleaning services for homes, offices and commercial spaces.", icon: SprayCan },
  { slug: "decorating", label: "Decorating", desc: "Bespoke event & room decoration for birthdays, proposals and celebrations.", icon: PaintRoller, key: "decorating" },
  { slug: "chauffeur", label: "Chauffeur", desc: "Luxury chauffeur services for airport transfers, events and private travel.", icon: UserRound },
  { slug: "concierge", label: "Concierge", desc: "Bespoke concierge services to simplify your life and save you time.", icon: Gem },
  { slug: "photography", label: "Photography", desc: "Photography & videography for events, brands, properties and portraits.", icon: Camera },
  { slug: "property-maintenance", label: "Property Maintenance", desc: "Reliable maintenance solutions to keep your property in perfect order.", icon: Wrench, key: "maintenance" },
  { slug: "removals", label: "Removal Services", desc: "Stress-free removal services for homes, offices and international moves.", icon: Truck },
  { slug: "catering", label: "Catering", desc: "Exquisite catering for private dining, events and corporate occasions.", icon: Utensils },
];

const TRUST_POINTS = [
  { icon: ShieldCheck, title: "Discreet & Trusted", body: "Your privacy and peace of mind are our top priority." },
  { icon: Clock, title: "Available 24/7", body: "We are here whenever and wherever you need us." },
  { icon: Gem, title: "Tailored Solutions", body: "Every service is customised to suit your lifestyle." },
  { icon: Award, title: "Exceptional Quality", body: "We deliver excellence in every detail of what we do." },
];

export default function Home() {
  const [services, setServices] = useState([]);
  const [form, setForm] = useState({ name: "", email: "", phone: "", service: "", date: "", time: "", notes: "" });
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    api.get("/services").then((r) => setServices(r.data)).catch(() => {});
  }, []);

  const serviceMap = useMemo(() => Object.fromEntries(services.map((s) => [s.slug, s])), [services]);

  const submitEnquiry = async (e) => {
    e.preventDefault();
    if (!form.service || !form.date || !form.name || !form.email) {
      toast.error("Please complete all required fields");
      return;
    }
    const svc = serviceMap[form.service];
    const pkg = svc?.packages?.[0];
    if (!svc || !pkg) return;
    setSubmitting(true);
    try {
      await api.post("/bookings", {
        service_slug: svc.slug,
        package_id: pkg.id,
        date: form.date,
        time: form.time || "10:00",
        location: "To be confirmed",
        notes: form.notes,
        guest_name: form.name,
        guest_email: form.email,
        guest_phone: form.phone,
      });
      toast.success("Booking request received. We'll be in touch shortly.");
      setForm({ name: "", email: "", phone: "", service: "", date: "", time: "", notes: "" });
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail) || err.message);
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div data-testid="home-page">
      <section className="relative min-h-[100vh] flex items-center overflow-hidden bg-[#0F0D0A]">
        <div className="absolute inset-0">
          <img src={HERO_IMG} alt="Luxor" className="w-full h-full object-cover image-scale-in" />
          <div className="absolute inset-0 bg-gradient-to-r from-[#0F0D0A]/95 via-[#0F0D0A]/60 to-transparent" />
          <div className="absolute inset-0 bg-gradient-to-t from-[#0F0D0A]/60 via-transparent to-transparent" />
        </div>
        <div className="relative z-10 max-w-[1400px] mx-auto px-6 lg:px-10 py-32 w-full">
          <motion.p initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.15, duration: 0.7 }} className="overline" data-testid="hero-overline">
            Welcome to Luxor Services &amp; Concierge
          </motion.p>
          <motion.h1 initial={{ opacity: 0, y: 30 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.3, duration: 0.9 }} className="font-serif text-5xl sm:text-6xl lg:text-7xl xl:text-8xl leading-[1.02] mt-6 text-[#F5EFE4] max-w-3xl" data-testid="hero-title">
            Luxury Concierge<br />Services Without Limits
          </motion.h1>
          <motion.p initial={{ opacity: 0, y: 30 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.55, duration: 0.9 }} className="mt-8 max-w-xl text-[#F5EFE4]/80 text-base leading-relaxed">
            Exceptional lifestyle, business and personal concierge services tailored to your needs.
          </motion.p>
          <motion.p initial={{ opacity: 0, y: 30 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.7, duration: 0.9 }} className="mt-4 font-serif italic text-xl text-[#F5EFE4]">
            Discreet. Professional. Reliable.
          </motion.p>
          <motion.div initial={{ opacity: 0, y: 30 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.85, duration: 0.9 }} className="mt-10 flex flex-wrap gap-4">
            <Link to="/book" className="luxor-btn-primary" data-testid="hero-reserve-btn">
              <CalendarDays size={14} /> Request a Consultation
            </Link>
            <Link to="/services" className="luxor-btn-outline-light" data-testid="hero-explore-btn">
              Our Services
            </Link>
          </motion.div>
          <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} transition={{ delay: 1.1, duration: 0.9 }} className="mt-10 flex items-center gap-3 text-[#C5A059] text-[11px] uppercase tracking-[0.24em]">
            <span className="w-8 h-8 rounded-full border border-[#C5A059] flex items-center justify-center">
              <CalendarDays size={13} />
            </span>
            Available by appointment
          </motion.div>
        </div>
      </section>

      <section className="py-24 lg:py-32 bg-[#F5EFE4]" data-testid="services-section">
        <div className="max-w-[1400px] mx-auto px-6 lg:px-10">
          <div className="text-center max-w-2xl mx-auto">
            <p className="overline">Our Services</p>
            <h2 className="font-serif text-4xl sm:text-5xl lg:text-6xl mt-4 leading-[1.05] text-[#1A1815]">How We Can Help You</h2>
            <div className="mt-6 flex justify-center"><span className="divider-gold-center"></span></div>
          </div>
          <div className="mt-16 grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-5 gap-4">
            {HOME_SERVICE_CARDS.map((s, i) => {
              const Icon = s.icon;
              return (
                <Link key={s.key || s.slug + i} to={"/services/" + s.slug} data-testid={"service-card-" + (s.key || s.slug)} className="group bg-white border border-[#E4D9C1] p-8 text-center transition-all duration-300 hover:border-[#C5A059] hover:shadow-[0_20px_40px_-20px_rgba(197,160,89,0.35)] hover:-translate-y-1 flex flex-col">
                  <div className="w-14 h-14 mx-auto rounded-full bg-[#F5EFE4] border border-[#E4D9C1] flex items-center justify-center text-[#1A1815] group-hover:bg-[#C5A059] group-hover:text-white group-hover:border-[#C5A059] transition-colors">
                    <Icon size={22} strokeWidth={1.5} />
                  </div>
                  <h3 className="mt-5 text-xs uppercase tracking-[0.22em] font-medium text-[#1A1815] font-sans">{s.label}</h3>
                  <p className="mt-3 text-[13px] text-[#6B665E] leading-relaxed flex-1">{s.desc}</p>
                  <span className="mt-5 text-[10px] uppercase tracking-[0.24em] text-[#C5A059] inline-flex items-center gap-1.5 justify-center">
                    Learn More <ArrowRight size={11} />
                  </span>
                </Link>
              );
            })}
          </div>
        </div>
      </section>

      <section className="relative bg-[#1A1815] py-24 lg:py-32 overflow-hidden" data-testid="home-booking-section">
        <div className="absolute inset-0 opacity-25" style={{ backgroundImage: "url('https://images.unsplash.com/photo-1618221118493-9cfa1a1c00da?crop=entropy&cs=srgb&fm=jpg&q=85&w=1800')", backgroundSize: "cover", backgroundPosition: "center" }} />
        <div className="absolute inset-0 bg-gradient-to-r from-[#1A1815] via-[#1A1815]/95 to-[#1A1815]/70" />
        <div className="relative z-10 max-w-[1400px] mx-auto px-6 lg:px-10 grid grid-cols-1 lg:grid-cols-2 gap-16">
          <div>
            <p className="overline">Book an Appointment</p>
            <h2 className="font-serif text-4xl lg:text-6xl mt-5 leading-[1.05] text-[#F5EFE4]">Book a Service</h2>
            <div className="mt-5 flex"><span className="divider-gold-center" /></div>
            <p className="mt-8 text-[#F5EFE4]/70 text-base leading-relaxed max-w-md">
              Schedule a service or consultation with our team. We are available 7 days a week to assist you.
            </p>
            <ul className="mt-10 space-y-4 text-sm text-[#F5EFE4]/85">
              {["Quick & Easy Booking", "Tailored to Your Needs", "Discreet & Professional"].map((t) => (
                <li key={t} className="flex items-center gap-3">
                  <CheckCircle2 size={16} className="text-[#C5A059]" /> {t}
                </li>
              ))}
            </ul>
          </div>
          <form onSubmit={submitEnquiry} className="grid grid-cols-1 sm:grid-cols-2 gap-4" data-testid="home-booking-form">
            <input className="luxor-field-dark" placeholder="Full Name" value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} data-testid="home-form-name" required />
            <input type="email" className="luxor-field-dark" placeholder="Email Address" value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} data-testid="home-form-email" required />
            <input className="luxor-field-dark" placeholder="Phone Number" value={form.phone} onChange={(e) => setForm({ ...form, phone: e.target.value })} data-testid="home-form-phone" />
            <select className="luxor-field-dark" value={form.service} onChange={(e) => setForm({ ...form, service: e.target.value })} data-testid="home-form-service" required>
              <option value="">Service Required</option>
              {services.map((s) => (<option key={s.slug} value={s.slug}>{s.name}</option>))}
            </select>
            <input type="date" className="luxor-field-dark" value={form.date} onChange={(e) => setForm({ ...form, date: e.target.value })} data-testid="home-form-date" required />
            <input type="time" className="luxor-field-dark" value={form.time} onChange={(e) => setForm({ ...form, time: e.target.value })} data-testid="home-form-time" />
            <textarea rows={4} className="luxor-field-dark sm:col-span-2 resize-none" placeholder="Additional Information" value={form.notes} onChange={(e) => setForm({ ...form, notes: e.target.value })} data-testid="home-form-notes" />
            <button type="submit" disabled={submitting} className="luxor-btn-primary sm:col-span-2 justify-center disabled:opacity-50 mt-2" data-testid="home-form-submit">
              {submitting ? "Sending…" : "Book Now"} <ArrowRight size={14} />
            </button>
          </form>
        </div>
      </section>

      <section className="bg-[#F5EFE4] py-16 border-t border-black/8">
        <div className="max-w-[1400px] mx-auto px-6 lg:px-10 grid grid-cols-1 md:grid-cols-4 gap-10">
          {TRUST_POINTS.map(({ icon: I, title, body }) => (
            <div key={title} className="flex gap-4 items-start">
              <div className="shrink-0 w-11 h-11 rounded-full border border-[#C5A059] flex items-center justify-center text-[#C5A059]">
                <I size={18} strokeWidth={1.5} />
              </div>
              <div>
                <p className="text-[11px] uppercase tracking-[0.22em] font-medium text-[#1A1815]">{title}</p>
                <p className="mt-1.5 text-sm text-[#6B665E] leading-relaxed">{body}</p>
              </div>
            </div>
          ))}
        </div>
      </section>

      <section className="bg-[#F5EFE4] py-20 border-t border-black/5">
        <div className="max-w-[1000px] mx-auto px-6 lg:px-10 text-center">
          <p className="overline">Ready when you are</p>
          <h2 className="font-serif text-4xl lg:text-5xl mt-4 text-[#1A1815]">Begin your relationship with Luxor.</h2>
          <p className="mt-4 text-[#6B665E] max-w-xl mx-auto">Discreet enquiries welcome. Our concierge team will respond within the hour.</p>
          <div className="mt-8 flex flex-wrap gap-4 justify-center">
            <Link to="/book" className="luxor-btn-primary" data-testid="cta-strip-reserve">
              <CalendarDays size={14} /> Book a Service
            </Link>
            <Link to="/services" className="luxor-btn-outline-dark" data-testid="cta-strip-services">Browse Services</Link>
          </div>
        </div>
      </section>
    </div>
  );
}
